import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import "../styles/components/ProcessCardPopup.css";
import "../styles/components/TransactionTable.css";
import { useOutsideClick } from "../utils/useOutsideClick";
import { fetchSettings } from "../apis/settings";
import {
  createProcessCard,
  fetchProcessCard,
  updateProcessCard,
} from "../apis/processCard";
import { displayToast } from "../utils/Toast";
import { ClipboardList, Edit2, Printer } from "lucide-react";
import Loader from "../components/Loader";

const ProcessCardPopup = ({
  processCardNo = "",
  productName = "",
  productDescription = "",
  onClose,
  process_card_created = false,
  onProcessCardSaved,
  process_card_id,
}) => {
  const navigate = useNavigate();
  const { tab, category } = useParams();
  const [rows, setRows] = useState([
    { operations: "", workStation: "", timeRequired: "", processOrder: "" },
  ]);
  const [operationsOptions, setOperationsOptions] = useState([]);
  const [workStationsOptions, setWorkStationsOptions] = useState([]);
  const [openDropdown, setOpenDropdown] = useState({ row: null, field: null });
  const dropdownRefs = useRef({});
  const dropdownMenuRef = useRef(null);
  const [dropdownWidth, setDropdownWidth] = useState(180);
  const [loadingOptions, setLoadingOptions] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [headData, setHeadData] = useState({
    product_id: processCardNo,
    product_name: productName,
    product_desc: productDescription,
    tran_no: processCardNo ? String(processCardNo) : "",
  });
  const [localToast, setLocalToast] = useState({ message: "", type: "" });
  const showLocalToast = (message, type = "error") => {
    setLocalToast({ message, type });
    setTimeout(() => setLocalToast({ message: "", type: "" }), 2500);
  };

  useEffect(() => {
    if (process_card_created && process_card_id) {
      (async () => {
        try {
          const response = await fetchProcessCard({ id: process_card_id });
          if (response && response.head_data) {
            setHeadData(response.head_data);
            setRows(
              Array.isArray(response.item_data)
                ? response.item_data.map((item) => ({
                    operations: item.operation,
                    workStation: item.work_station,
                    timeRequired: item.time_required,
                    processOrder: item.process_order,
                  }))
                : []
            );
          }
        } catch (err) {
          showLocalToast("Failed to fetch process card", "error");
        }
      })();
    }
  }, [process_card_created, process_card_id]);

  useEffect(() => {
    if (openDropdown.row === null) return;
    function handleClick(e) {
      const triggerKey = `${openDropdown.field}-${openDropdown.row}`;
      const triggerEl = dropdownRefs.current[triggerKey];
      if (
        dropdownMenuRef.current &&
        !dropdownMenuRef.current.contains(e.target) &&
        (!triggerEl || !triggerEl.contains(e.target))
      ) {
        setOpenDropdown({ row: null, field: null });
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [openDropdown]);

  useEffect(() => {
    if (loadingOptions) return;
    const getMaxWidth = (options) => {
      if (!options || options.length === 0) return 180;
      const temp = document.createElement("span");
      temp.style.visibility = "hidden";
      temp.style.position = "absolute";
      temp.style.fontSize = "16px";
      temp.style.fontFamily = "inherit";
      document.body.appendChild(temp);
      let max = 180;
      options.forEach((opt) => {
        temp.textContent = opt.name;
        max = Math.max(max, temp.offsetWidth + 48);
      });
      document.body.removeChild(temp);
      return max;
    };
    const opsW = getMaxWidth(operationsOptions);
    const wsW = getMaxWidth(workStationsOptions);
    setDropdownWidth(Math.max(opsW, wsW));
  }, [operationsOptions, workStationsOptions, loadingOptions]);

  useEffect(() => {
    async function fetchDropdowns() {
      try {
        setLoadingOptions(true);
        const operationsRes = await fetchSettings("operations");
        const workStationsRes = await fetchSettings("work_stations");
        setOperationsOptions(operationsRes.items || []);
        setWorkStationsOptions(workStationsRes.items || []);
      } catch (err) {
        setOperationsOptions([]);
        setWorkStationsOptions([]);
      } finally {
        setLoadingOptions(false);
      }
    }
    fetchDropdowns();
  }, []);

  const handleAddRow = () => {
    setRows([
      ...rows,
      { operations: "", workStation: "", timeRequired: "", processOrder: "" },
    ]);
  };

  const handleInputChange = (index, field, value) => {
    const updatedRows = [...rows];
    updatedRows[index][field] = value;
    setRows(updatedRows);
  };

  const handleRemoveRow = (index) => {
    if (rows.length === 1) return;
    setRows(rows.filter((_, i) => i !== index));
  };

  const handlePrintClick = () => {
    navigate(`/${tab || "master"}/${category || "product"}/transactions/invoice`, {
      state: {
        formData: {
          headDetails: {
            process_card_no: processCardNo,
            product_name: productName,
            product_desc: productDescription,
          },
          itemDetails: rows.map((row, index) => ({
            serial_no: index + 1,
            operation: row.operations,
            work_station: row.workStation,
            time_required: row.timeRequired,
            process_order: row.processOrder,
          })),
          rowData: [
            { field_name: "operation", title: "Operations" },
            { field_name: "work_station", title: "Work Station" },
            { field_name: "time_required", title: "Time Required" },
            { field_name: "process_order", title: "Process Order" },
          ],
          transactionCode: "PROCESS_CARD",
        },
      },
    });
  };

  const handleSave = async () => {
    for (let i = 0; i < rows.length; i++) {
      const row = rows[i];
      if (
        !row.operations ||
        !row.workStation ||
        !row.timeRequired ||
        !row.processOrder
      ) {
        showLocalToast(
          `All fields are required in every row. Please fill missing fields.`,
          "error"
        );
        return;
      }
    }
    setIsSaving(true);
    try {
      const head_data = {
        product_id: headData.product_id,
        product_name: headData.product_name,
        product_desc: headData.product_desc,
        tran_no: headData.tran_no,
      };
      const item_data = rows.map((row, idx) => ({
        serial_no: idx + 1,
        operation: row.operations,
        work_station: row.workStation,
        time_required: row.timeRequired ? parseInt(row.timeRequired, 10) : 0,
        process_order: row.processOrder ? parseInt(row.processOrder, 10) : 0,
      }));
      const submissionData = { head_data, item_data };
      let response;
      if (process_card_created && isEditMode && process_card_id) {
        response = await updateProcessCard(submissionData, process_card_id);
      } else {
        response = await createProcessCard(submissionData);
      }
      if (response && !response.description) {
        showLocalToast(
          process_card_created && isEditMode
            ? "Process Card updated successfully"
            : "Process Card created successfully",
          "success"
        );
        if (onProcessCardSaved) onProcessCardSaved();
        if (onClose) onClose();
      } else {
        showLocalToast(
          response?.description || "Failed to save process card",
          "error"
        );
      }
    } catch (err) {
      showLocalToast("Failed to save process card", "error");
    } finally {
      setIsSaving(false);
    }
  };

  const popupRef = useOutsideClick(() => {
    if (onClose) onClose();
  });

  const getDropdownFixedStyle = (triggerEl) => {
    if (!triggerEl) return { left: 0, top: 0, width: 200 };
    const rect = triggerEl.getBoundingClientRect();
    return {
      left: rect.left,
      top: rect.bottom + 4,
      width: rect.width,
    };
  };

  return (
    <div className="process-card-popup-overlay">
      <div className="process-card-popup-container" ref={popupRef}>
        {localToast.message && (
          <div className={`process-card-toast ${localToast.type}`}>
            {localToast.message}
          </div>
        )}
        <div className="process-card-popup-header">
          <h3 style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <ClipboardList
              size={24}
              color="#ffffff"
              style={{ marginRight: 4 }}
            />
            Process Card
          </h3>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            {process_card_created && !isEditMode && (
              <Edit2
                style={{ cursor: "pointer" }}
                size={22}
                onClick={() => setIsEditMode(true)}
                title="Edit Process Card"
              />
            )}
            <Printer
              style={{ cursor: "pointer" }}
              size={22}
              onClick={handlePrintClick}
              title="Print Process Card"
            />
            {onClose && (
              <button className="process-card-popup-close" onClick={onClose}>
                &times;
              </button>
            )}
          </div>
        </div>
        <div className="process-card-popup-head-section">
          <div className="process-card-popup-head-field">
            <label>Process Card No.</label>
            <div className="process-card-popup-head-value min-line">
              {processCardNo || "-"}
            </div>
          </div>
          <div className="process-card-popup-head-field">
            <label>Product Name</label>
            <div className="process-card-popup-head-value min-line">
              {productName || "-"}
            </div>
          </div>
          <div className="process-card-popup-head-field">
            <label>Product Description</label>
            <div className="process-card-popup-head-value min-line">
              {productDescription || "-"}
            </div>
          </div>
        </div>
        <div className="transaction-card">
          <div
            className="transaction-card-content"
            style={{ position: "relative" }}
          >
            <div className="table-container">
              <table className="transaction-table">
                <thead>
                  <tr>
                    <th>Sl.No</th>
                    <th>
                      Operations
                      <span style={{ color: "#222", fontSize: "13px" }}>
                        {" "}
                        *
                      </span>
                    </th>
                    <th>
                      Work Station
                      <span style={{ color: "#222", fontSize: "13px" }}>
                        {" "}
                        *
                      </span>
                    </th>
                    <th>
                      Time Required
                      <span style={{ color: "#222", fontSize: "13px" }}>
                        {" "}
                        *
                      </span>
                    </th>
                    <th>
                      Process Order
                      <span style={{ color: "#222", fontSize: "13px" }}>
                        {" "}
                        *
                      </span>
                    </th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, idx) => (
                    <tr key={idx}>
                      <td>{idx + 1}</td>
                      <td>
                        <div
                          className={`modal-dropdown-container`}
                          style={{ position: "relative", width: dropdownWidth }}
                        >
                          <div
                            ref={(el) =>
                              (dropdownRefs.current[`operations-${idx}`] = el)
                            }
                            className={`customer-data-modal-input master-data-modal-select-wrapper${
                              openDropdown.row === idx &&
                              openDropdown.field === "operations"
                                ? " dropdown-open-highlight"
                                : ""
                            }`}
                            style={{
                              width: dropdownWidth,
                              pointerEvents:
                                !process_card_created || isEditMode
                                  ? "auto"
                                  : "none",
                              background:
                                process_card_created && !isEditMode
                                  ? "#f9fafb"
                                  : undefined,
                            }}
                            onClick={() => {
                              if (!process_card_created || isEditMode)
                                setOpenDropdown(
                                  openDropdown.row === idx &&
                                    openDropdown.field === "operations"
                                    ? { row: null, field: null }
                                    : { row: idx, field: "operations" }
                                );
                            }}
                            tabIndex={0}
                          >
                            <span className="modal-dropdown-text">
                              {row.operations || "Select Operation"}
                            </span>
                            {(!process_card_created || isEditMode) && (
                              <span className="dropdown-icon">▼</span>
                            )}
                          </div>
                          {openDropdown.row === idx &&
                            openDropdown.field === "operations" &&
                            dropdownRefs.current[`operations-${idx}`] && (
                              <div
                                ref={dropdownMenuRef}
                                className="primary-dropdown-options process-card-popup-dropdown-fixed"
                                style={getDropdownFixedStyle(
                                  dropdownRefs.current[`operations-${idx}`]
                                )}
                              >
                                {operationsOptions.map((opt) => (
                                  <div
                                    key={opt.id}
                                    className={`primary-dropdown-option${
                                      row.operations === opt.name
                                        ? " selected-option"
                                        : ""
                                    }`}
                                    onClick={() => {
                                      handleInputChange(
                                        idx,
                                        "operations",
                                        opt.name
                                      );
                                      setOpenDropdown({
                                        row: null,
                                        field: null,
                                      });
                                    }}
                                  >
                                    {opt.name}
                                  </div>
                                ))}
                              </div>
                            )}
                        </div>
                      </td>
                      <td>
                        <div
                          className={`modal-dropdown-container`}
                          style={{ position: "relative", width: dropdownWidth }}
                        >
                          <div
                            ref={(el) =>
                              (dropdownRefs.current[`workStation-${idx}`] = el)
                            }
                            className={`customer-data-modal-input master-data-modal-select-wrapper${
                              openDropdown.row === idx &&
                              openDropdown.field === "workStation"
                                ? " dropdown-open-highlight"
                                : ""
                            }`}
                            style={{
                              width: dropdownWidth,
                              pointerEvents:
                                !process_card_created || isEditMode
                                  ? "auto"
                                  : "none",
                              background:
                                process_card_created && !isEditMode
                                  ? "#f9fafb"
                                  : undefined,
                            }}
                            onClick={() => {
                              if (!process_card_created || isEditMode)
                                setOpenDropdown(
                                  openDropdown.row === idx &&
                                    openDropdown.field === "workStation"
                                    ? { row: null, field: null }
                                    : { row: idx, field: "workStation" }
                                );
                            }}
                            tabIndex={0}
                          >
                            <span className="modal-dropdown-text">
                              {row.workStation || "Select Work Station"}
                            </span>
                            {(!process_card_created || isEditMode) && (
                              <span className="dropdown-icon">▼</span>
                            )}
                          </div>
                          {openDropdown.row === idx &&
                            openDropdown.field === "workStation" &&
                            dropdownRefs.current[`workStation-${idx}`] && (
                              <div
                                ref={dropdownMenuRef}
                                className="primary-dropdown-options process-card-popup-dropdown-fixed"
                                style={getDropdownFixedStyle(
                                  dropdownRefs.current[`workStation-${idx}`]
                                )}
                              >
                                {workStationsOptions.map((opt) => (
                                  <div
                                    key={opt.id}
                                    className={`primary-dropdown-option${
                                      row.workStation === opt.name
                                        ? " selected-option"
                                        : ""
                                    }`}
                                    onClick={() => {
                                      handleInputChange(
                                        idx,
                                        "workStation",
                                        opt.name
                                      );
                                      setOpenDropdown({
                                        row: null,
                                        field: null,
                                      });
                                    }}
                                  >
                                    {opt.name}
                                  </div>
                                ))}
                              </div>
                            )}
                        </div>
                      </td>
                      <td>
                        <input
                          type="text"
                          value={row.timeRequired}
                          onChange={(e) =>
                            handleInputChange(
                              idx,
                              "timeRequired",
                              e.target.value
                            )
                          }
                          className="custom-input"
                          placeholder="Time Required"
                          disabled={process_card_created && !isEditMode}
                        />
                      </td>
                      <td>
                        <input
                          type="text"
                          value={row.processOrder}
                          onChange={(e) =>
                            handleInputChange(
                              idx,
                              "processOrder",
                              e.target.value
                            )
                          }
                          className="custom-input"
                          placeholder="Process Order"
                          disabled={process_card_created && !isEditMode}
                        />
                      </td>
                      <td>
                        <button
                          className="remove-row-btn"
                          onClick={() => handleRemoveRow(idx)}
                          disabled={
                            rows.length === 1 ||
                            (process_card_created && !isEditMode)
                          }
                          title="Remove Row"
                        >
                          &times;
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="add-item-container-process-card">
              <button
                className="add-item-btn-process-card"
                onClick={handleAddRow}
                disabled={process_card_created && !isEditMode}
              >
                Add Item
                <svg
                  className="plus-icon"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <line x1="12" y1="5" x2="12" y2="19"></line>
                  <line x1="5" y1="12" x2="19" y2="12"></line>
                </svg>
              </button>
            </div>
            <div className="table-save-button-container-process-card">
              <button
                className="next-btn-process-card"
                onClick={handleSave}
                disabled={isSaving || (process_card_created && !isEditMode)}
              >
                {isSaving ? "Saving..." : "SAVE"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProcessCardPopup;
